import React from "react";

import { usePoweredByWhitelabel } from "@app/context/whitelabel-context";
import { getPurchaseTrip } from "@app/helpers/purchase/get-purchase-trip";
import {
  hasUpcomingOutbound,
  hasUpcomingReturn
} from "@app/helpers/udm/has-upcoming-leg";
import { isRoundtrip } from "@app/helpers/udm/is-roundtrip";
import type { CurrencyCode } from "@app/types/currency";
import type { NapiUser } from "@app/types/napi-types";
import type { NapiV2Purchase } from "@app/types/napi-v2-purchase-types";

import type { DynamicWhitelabel } from "@partners/partner.type";

import { NoTrips } from "./no-trips";
import { TripList } from "./trip-list";
import { AccountLayout } from "../account-layout";
import { AccountVerification } from "../account-verification";

export interface Props {
  user: NapiUser;
  locale: string;
  currency: CurrencyCode;
  purchases: NapiV2Purchase[];
}

const purchaseIsUpcoming = (purchase: NapiV2Purchase) => {
  const trip = getPurchaseTrip(purchase);

  const upcoming_outbound = hasUpcomingOutbound(trip);
  const upcoming_return = isRoundtrip(trip) ? hasUpcomingReturn(trip) : true;

  return upcoming_outbound && upcoming_return;
};

const purchaseIsCancelled = (purchase: NapiV2Purchase) => {
  return !!purchase.cancellation;
};

type FilteredTrips = {
  past_purchase: NapiV2Purchase[];
  upcoming_purchase: NapiV2Purchase[];
};

export const filterPurchases = (
  purchases: NapiV2Purchase[],
  whitelabel: DynamicWhitelabel | null
): FilteredTrips =>
  purchases.reduce(
    (filtered_purchases: FilteredTrips, purchase: NapiV2Purchase) => {
      // For whitelabels, filter out purchases that are not from the current e-commerce partner
      if (whitelabel && purchase.partner?.id !== whitelabel.partner_id) {
        return filtered_purchases;
      }

      purchaseIsUpcoming(purchase) && !purchaseIsCancelled(purchase)
        ? filtered_purchases.upcoming_purchase.push(purchase)
        : filtered_purchases.past_purchase.unshift(purchase);
      return filtered_purchases;
    },
    {
      past_purchase: [],
      upcoming_purchase: []
    }
  );

export const TripsApp: React.FC<Props> = ({ purchases, user }) => {
  const whitelabel = usePoweredByWhitelabel();
  const { past_purchase, upcoming_purchase } = filterPurchases(
    purchases,
    whitelabel
  );

  return (
    <AccountLayout current_page="trips">
      <div className="flex flex-col gap-600">
        <AccountVerification user={user} />
        {purchases.length === 0 ? (
          <NoTrips />
        ) : (
          <div className="flex flex-col gap-600">
            <TripList purchases={upcoming_purchase} />
            <TripList purchases={past_purchase} archived />
          </div>
        )}
      </div>
    </AccountLayout>
  );
};
